import { connectionInfoAtom } from "@/data/integration";
import {
  getIntegrationConfigurationMetadataQueryOptions,
  getIntegrationConnectionInfoQueryOptions,
  getIntegrationSettingsQueryOptions,
  getProviderCapabilitiesQueryOptions,
} from "@/queries/integrations";
import {
  ProviderCapabilities,
  ProviderConfigurationMetadata,
  ProviderConnectionInfo,
} from "@/types/integrations";
import { QueryClient } from "@tanstack/react-query";
import { useAtom } from "jotai";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export type IntegrationSetupStep =
  | "authentication"
  | "fields"
  | "settings"
  | "notifications"
  | "success";

export type IntegrationSetupMode = "setup" | "update";

export interface IntegrationSetupContextValue {
  /** Integration provider name (e.g., "hubspot", "salesforce") */
  integration: string;
  /** Whether this is a new setup or updating existing */
  mode: IntegrationSetupMode;
  /** Unique key for scoping session storage (e.g., "campaign-123-hubspot" or "hubspot") */
  storageKey: string;
  /** Current step in the wizard */
  currentStep: IntegrationSetupStep;
  /** Provider capabilities from API */
  capabilities: ProviderCapabilities | null;
  /** Configuration metadata (auth fields, etc.) */
  configMetadata: ProviderConfigurationMetadata[] | null;
  /** Whether the integration has custom settings */
  hasSettings: boolean;
  /** Connection info being built up across steps */
  connectionInfo: ProviderConnectionInfo | null;
  /** Set connection info */
  setConnectionInfo: (info: ProviderConnectionInfo | null) => void;
  /** OAuth response channel ID for OAuth flows */
  oauthResponseChannelId: string;
  /** Navigate to a specific step */
  goToStep: (step: IntegrationSetupStep) => void;
  /** Go to next step based on current position and capabilities */
  goToNextStep: () => void;
  /** Go to previous step */
  goToPreviousStep: () => void;
  /** Called when wizard completes successfully */
  onComplete: (updatedConnectionInfo?: ProviderConnectionInfo) => void;
  /** Called when wizard is cancelled */
  onCancel: () => void;
  /** Get session storage key for a specific data type */
  getSessionKey: (type: "auth" | "fields" | "settings") => string;
  /** Clear all session storage for this setup */
  clearSessionStorage: () => void;
  /** Total number of steps (varies based on capabilities) */
  totalSteps: number;
  /** Current step number (1-indexed) */
  currentStepNumber: number;
}

const IntegrationSetupContext =
  createContext<IntegrationSetupContextValue | null>(null);

export interface IntegrationSetupProviderProps {
  children: ReactNode;
  /** Integration provider name */
  integration: string;
  /** Setup or update mode */
  mode: IntegrationSetupMode;
  /** Optional prefix for session storage keys (e.g., "campaign-123") */
  storageKeyPrefix?: string;
  /** Initial step to start on */
  initialStep?: IntegrationSetupStep;
  /** Called when the setup wizard completes */
  onComplete?: (connectionInfo: ProviderConnectionInfo) => void;
  /** Called when the user cancels the wizard */
  onCancel?: () => void;
  /** Pre-loaded capabilities (optional, will fetch if not provided) */
  capabilities?: ProviderCapabilities;
  /** Pre-loaded config metadata (optional, will fetch if not provided) */
  configMetadata?: ProviderConfigurationMetadata[];
  /** Pre-loaded hasSettings (optional, will compute if not provided) */
  hasSettings?: boolean;
  /** Existing connection info to seed the wizard with (required for update mode) */
  connectionInfo?: ProviderConnectionInfo | null;
}

function getStepOrder(
  capabilities: ProviderCapabilities | null,
  hasSettings: boolean,
  mode: IntegrationSetupMode,
): IntegrationSetupStep[] {
  // For storage-only integrations, only need authentication
  if (capabilities?.storage) {
    return ["authentication", "success"];
  }

  const steps: IntegrationSetupStep[] = ["authentication", "fields"];

  if (hasSettings) {
    steps.push("settings");
  }

  // Notifications are only available in update mode
  if (mode === "update") {
    steps.push("notifications");
  }

  steps.push("success");

  return steps;
}

export function IntegrationSetupProvider({
  children,
  integration,
  mode,
  storageKeyPrefix,
  initialStep = "authentication",
  onComplete: onCompleteProp,
  onCancel: onCancelProp,
  capabilities: preloadedCapabilities,
  configMetadata: preloadedConfigMetadata,
  hasSettings: preloadedHasSettings,
  connectionInfo: preloadedConnectionInfo,
}: IntegrationSetupProviderProps) {
  const [currentStep, setCurrentStep] =
    useState<IntegrationSetupStep>(initialStep);
  const [connectionInfo, setConnectionInfoState] = useAtom(connectionInfoAtom);
  const [oauthResponseChannelId] = useState(() => crypto.randomUUID());

  // Seed the atom with pre-loaded connection info (e.g. for update mode).
  // Only runs once on mount — subsequent changes are managed by the wizard steps.
  useEffect(() => {
    if (preloadedConnectionInfo != null) {
      setConnectionInfoState(preloadedConnectionInfo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Internal state for loaded data (if not preloaded)
  const [capabilities] = useState<ProviderCapabilities | null>(
    preloadedCapabilities ?? null,
  );
  const [configMetadata] = useState<ProviderConfigurationMetadata[] | null>(
    preloadedConfigMetadata ?? null,
  );
  const [hasSettings] = useState(preloadedHasSettings ?? false);

  // Storage key for scoping session storage
  const storageKey = useMemo(() => {
    if (storageKeyPrefix) {
      return `${storageKeyPrefix}-${integration}`;
    }
    return integration;
  }, [storageKeyPrefix, integration]);

  const getSessionKey = useCallback(
    (type: "auth" | "fields" | "settings") => {
      return `integration-${storageKey}-${type}`;
    },
    [storageKey],
  );

  const clearSessionStorage = useCallback(() => {
    sessionStorage.removeItem(getSessionKey("auth"));
    sessionStorage.removeItem(getSessionKey("fields"));
    sessionStorage.removeItem(getSessionKey("settings"));
    setConnectionInfoState(null);
  }, [getSessionKey, setConnectionInfoState]);

  const setConnectionInfo = useCallback(
    (info: ProviderConnectionInfo | null) => {
      setConnectionInfoState(info);
    },
    [setConnectionInfoState],
  );

  const stepOrder = useMemo(
    () => getStepOrder(capabilities, hasSettings, mode),
    [capabilities, hasSettings, mode],
  );

  const totalSteps = useMemo(() => {
    // Don't count "success" as a user-facing step
    return stepOrder.filter((s) => s !== "success").length;
  }, [stepOrder]);

  const currentStepNumber = useMemo(() => {
    const idx = stepOrder.indexOf(currentStep);
    // Don't count success as a step number
    if (currentStep === "success") return totalSteps;
    return idx + 1;
  }, [stepOrder, currentStep, totalSteps]);

  const goToStep = useCallback((step: IntegrationSetupStep) => {
    setCurrentStep(step);
  }, []);

  const goToNextStep = useCallback(() => {
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex < stepOrder.length - 1) {
      setCurrentStep(stepOrder[currentIndex + 1]);
    }
  }, [stepOrder, currentStep]);

  const goToPreviousStep = useCallback(() => {
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(stepOrder[currentIndex - 1]);
    }
  }, [stepOrder, currentStep]);

  const onComplete = useCallback(
    (updatedConnectionInfo?: ProviderConnectionInfo) => {
      const infoToUse = updatedConnectionInfo ?? connectionInfo;
      if (infoToUse && onCompleteProp) {
        onCompleteProp(infoToUse);
      }
      clearSessionStorage();
    },
    [connectionInfo, onCompleteProp, clearSessionStorage],
  );

  const onCancel = useCallback(() => {
    clearSessionStorage();
    onCancelProp?.();
  }, [clearSessionStorage, onCancelProp]);

  const value = useMemo<IntegrationSetupContextValue>(
    () => ({
      integration,
      mode,
      storageKey,
      currentStep,
      capabilities,
      configMetadata,
      hasSettings,
      connectionInfo,
      setConnectionInfo,
      oauthResponseChannelId,
      goToStep,
      goToNextStep,
      goToPreviousStep,
      onComplete,
      onCancel,
      getSessionKey,
      clearSessionStorage,
      totalSteps,
      currentStepNumber,
    }),
    [
      integration,
      mode,
      storageKey,
      currentStep,
      capabilities,
      configMetadata,
      hasSettings,
      connectionInfo,
      setConnectionInfo,
      oauthResponseChannelId,
      goToStep,
      goToNextStep,
      goToPreviousStep,
      onComplete,
      onCancel,
      getSessionKey,
      clearSessionStorage,
      totalSteps,
      currentStepNumber,
    ],
  );

  return (
    <IntegrationSetupContext.Provider value={value}>
      {children}
    </IntegrationSetupContext.Provider>
  );
}

export function useIntegrationSetup(): IntegrationSetupContextValue {
  const context = useContext(IntegrationSetupContext);
  if (!context) {
    throw new Error(
      "useIntegrationSetup must be used within an IntegrationSetupProvider",
    );
  }
  return context;
}

/**
 * Helper to load integration setup data for use with IntegrationSetupProvider
 */
export async function loadIntegrationSetupData(
  queryClient: QueryClient,
  integration: string,
  mode: IntegrationSetupMode,
): Promise<{
  capabilities: ProviderCapabilities;
  configMetadata: ProviderConfigurationMetadata[];
  connectionInfo: ProviderConnectionInfo | null;
  hasSettings: boolean;
}> {
  const [capabilities, configMetadata, connectionInfo] = await Promise.all([
    queryClient.ensureQueryData(
      getProviderCapabilitiesQueryOptions(integration),
    ),
    queryClient.ensureQueryData(
      getIntegrationConfigurationMetadataQueryOptions(integration),
    ),
    queryClient.ensureQueryData(
      getIntegrationConnectionInfoQueryOptions(integration),
    ),
  ]);

  let hasSettings = false;
  if (mode !== "setup") {
    const settings = await queryClient.ensureQueryData(
      getIntegrationSettingsQueryOptions(integration),
    );
    hasSettings = !!settings && settings.length > 0;
  }

  return {
    capabilities,
    configMetadata,
    connectionInfo,
    hasSettings,
  };
}
